import type { JSX } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Icon } from '../primitives/Icon';
import { useSessionStore } from '@/state/sessionStore';
import type { LockTab } from '@/state/sessionStore';
import { PATHS, lockerroomTab } from '@/routes/paths';
import { Logo } from './Logo';

interface RailEntry {
  readonly id: string;
  readonly label: string;
  readonly icon: string;
  readonly href: string;
}

const ZONES: readonly RailEntry[] = [
  { id: 'arena', label: 'Arena', icon: 'trophy', href: PATHS.arena },
  { id: 'game', label: 'Game', icon: 'goal', href: PATHS.game },
  { id: 'lockerroom', label: 'Lockerroom', icon: 'clipboard', href: PATHS.lockerroom },
  { id: 'front-office', label: 'Front Office', icon: 'handshake', href: PATHS.frontOffice },
  { id: 'gym', label: 'Gym', icon: 'dumbbell', href: PATHS.gym },
];

const VIEWS: readonly (RailEntry & { readonly tab: LockTab })[] = [
  {
    id: 'v-reports',
    label: 'Reports',
    icon: 'fileCheck',
    href: lockerroomTab('reports'),
    tab: 'reports',
  },
  { id: 'v-agents', label: 'Agents', icon: 'bot', href: lockerroomTab('agents'), tab: 'agents' },
  {
    id: 'v-library',
    label: 'Library',
    icon: 'library',
    href: lockerroomTab('library'),
    tab: 'library',
  },
  {
    id: 'v-external',
    label: 'External research',
    icon: 'book',
    href: lockerroomTab('external'),
    tab: 'external',
  },
];

/**
 * Which zone the current route belongs to.
 *
 * Every agent and report lives under the Lockerroom, so the rail keeps that
 * zone marked while an agent is open — otherwise the user loses their place the
 * moment they start a run.
 */
export function activeZone(pathname: string): string {
  if (
    pathname.startsWith('/agent') ||
    pathname.startsWith('/report') ||
    pathname === PATHS.lockerroom
  ) {
    return 'lockerroom';
  }
  // Admin sits outside the zone model on purpose: it is not somewhere a user works, so
  // nothing in the rail should light up and imply it is.
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname === PATHS.gym) return 'gym';
  if (pathname === PATHS.game) return 'game';
  if (pathname === PATHS.frontOffice) return 'front-office';
  return 'arena';
}

export function Rail({ userName }: { readonly userName: string }): JSX.Element {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const lockTab = useSessionStore((s) => s.lock.tab);
  const lockTo = useSessionStore((s) => s.lockTo);

  const zone = activeZone(pathname);
  const inLockerroom = zone === 'lockerroom';

  return (
    <nav className="pnet-rail" aria-label="Primary">
      <button
        type="button"
        className="rail-logo"
        onClick={() => {
          void navigate(PATHS.arena);
        }}
        title="Arena — home"
      >
        <Logo />
      </button>

      <div className="rail-group">
        <span className="rail-group-label">Tools</span>
        {ZONES.map((z) => (
          <button
            key={z.id}
            type="button"
            className="rail-btn"
            aria-current={zone === z.id}
            aria-label={z.label}
            onClick={() => {
              void navigate(z.href);
            }}
          >
            <Icon name={z.icon} />
            <span className="rail-tip">{z.label}</span>
          </button>
        ))}
      </div>

      <div className="rail-group">
        <span className="rail-group-label">Views</span>
        {VIEWS.map((v) => (
          <button
            key={v.id}
            type="button"
            className="rail-btn"
            aria-current={inLockerroom && lockTab === v.tab}
            aria-label={v.label}
            onClick={() => {
              lockTo(v.tab);
              void navigate(v.href);
            }}
          >
            <Icon name={v.icon} />
            <span className="rail-tip">{v.label}</span>
          </button>
        ))}
      </div>

      <div className="rail-spacer" />

      <button
        type="button"
        className="rail-btn"
        aria-label="Settings"
        aria-current={pathname === PATHS.settings}
        onClick={() => {
          void navigate(PATHS.settings);
        }}
      >
        <Icon name="settings" />
        <span className="rail-tip">Settings</span>
      </button>
      {/* The identity block. `rail-foot` is the hook the "Collapse profile
          rail" tweak hides — see components.css. */}
      <button
        type="button"
        className="rail-btn rail-foot"
        aria-label="Account"
        onClick={() => {
          void navigate(PATHS.account);
        }}
      >
        <Icon name="user" />
        <span className="rail-tip">{userName}</span>
      </button>
    </nav>
  );
}
